import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {Select} from 'antd';
import {getViewportPopupLayout} from './viewportLayout';

const SELECT_CHROME = 8;

const popupPlacements = {
  bottomLeft: {
    points: ['tl', 'bl'],
    offset: [0, 4],
    overflow: {adjustX: true, adjustY: false, shiftY: true},
    htmlRegion: 'visible',
  },
  bottomRight: {
    points: ['tr', 'br'],
    offset: [0, 4],
    overflow: {adjustX: true, adjustY: false, shiftY: true},
    htmlRegion: 'visible',
  },
  topLeft: {
    points: ['bl', 'tl'],
    offset: [0, -4],
    overflow: {adjustX: true, adjustY: false, shiftY: true},
    htmlRegion: 'visible',
  },
  topRight: {
    points: ['br', 'tr'],
    offset: [0, -4],
    overflow: {adjustX: true, adjustY: false, shiftY: true},
    htmlRegion: 'visible',
  },
};

const isPopupScroll = (target) =>
  target instanceof Element &&
  Boolean(target.closest('.ant-select-dropdown, .ant-dropdown'));

const ViewportSelect = forwardRef((props, ref) => {
  const {
    className,
    popupClassName,
    dropdownClassName,
    placement: placementProp,
    listHeight: listHeightProp,
    onDropdownVisibleChange,
    onOpenChange,
    getPopupContainer,
    ...rest
  } = props;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState(placementProp || 'bottomLeft');
  const [listHeight, setListHeight] = useState(listHeightProp || 256);

  const readLayout = useCallback(() => {
    const next = getViewportPopupLayout(anchorRef.current);
    return {
      placement: placementProp || next.placement,
      listHeight:
        listHeightProp ||
        Math.max(next.maxHeight - SELECT_CHROME, 48),
    };
  }, [listHeightProp, placementProp]);

  const applyLayout = useCallback(() => {
    const next = readLayout();
    setPlacement((current) =>
      current === next.placement ? current : next.placement,
    );
    setListHeight((current) =>
      current === next.listHeight ? current : next.listHeight,
    );
  }, [readLayout]);

  useLayoutEffect(() => {
    if (open) {
      applyLayout();
    }
  }, [open, applyLayout]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onLayout = (event) => {
      if (isPopupScroll(event?.target)) {
        return;
      }
      applyLayout();
    };
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);
    return () => {
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [open, applyLayout]);

  return (
    <span
      ref={anchorRef}
      className='viewport-select-anchor'
      onPointerDownCapture={applyLayout}>
      <Select
        ref={ref}
        {...rest}
        className={className}
        placement={placement}
        listHeight={listHeight}
        builtinPlacements={popupPlacements}
        popupClassName={[
          'viewport-dropdown-menu',
          popupClassName || dropdownClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        getPopupContainer={getPopupContainer || (() => document.body)}
        onDropdownVisibleChange={(nextOpen) => {
          if (nextOpen) {
            applyLayout();
          }
          setOpen(nextOpen);
          onDropdownVisibleChange?.(nextOpen);
          onOpenChange?.(nextOpen);
        }}
      />
    </span>
  );
});

ViewportSelect.displayName = 'ViewportSelect';

export default ViewportSelect;
